import { VehicleOwnershipStatusEnum } from "@/lib/enum/vehicle-ownership-status.enum";
import { VehicleStateEnum } from "@/lib/enum/vehicleState.enum";
import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { AbstractPageDataProvider } from "@/lib/utility/data/page-data-provider.abstract";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { IPageViewModel } from "@/lib/utility/data/page-view-model.interface";
import { IVehicle, Vehicle } from "@/models/vehicle.entity";
import vehicleFleetService from "@/services/mrfiktiv/services/fleet/vehicleFleetService";
import fleetVehicleContractService from "@/services/mrfiktiv/services/fleet/fleetVehicleContractService";
import {
  MrfiktivFleetVehicleControllerGetAllParamsGen,
  MrfiktivVehicleViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { MrfiktivVehicleContractViewModelGen } from "@/services/mrfiktiv/services/fleet/fleetVehicleContractService";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { FleetVehicleDataAccessLayer } from "../access-layers/fleet-vehicle.access-layer";
import { PaginationFilterListElement } from "../base-pagination.store";
import { FleetManagerModule } from "./manager-fleet.store";
import { VehicleFinancingTypeEnum } from "@/lib/enum/vehicle-financing-type.enum";
import { FilterTypes } from "@/lib/filterable";

@Module({
  dynamic: true,
  namespaced: true,
  name: "vehicleFleetStore",
  store
})
export class VehicleFleetStore extends PaginatedBaseStore<
  IVehicle,
  MrfiktivFleetVehicleControllerGetAllParamsGen & { fleetIdFilter?: string[] }
> {
  _data = FleetVehicleDataAccessLayer;
  _pageProvider = new (class extends AbstractPageDataProvider<IVehicle, MrfiktivFleetVehicleControllerGetAllParamsGen> {
    async getPage(query: MrfiktivFleetVehicleControllerGetAllParamsGen): Promise<IPageViewModel<IVehicle>> {
      const res = await vehicleFleetService.findAll(query, { ids: FleetManagerModule.selectedFleetIds ?? [] });

      const meta = res.meta;
      const data = (res.data?.map((d) => new Vehicle(d)) ?? []) as IVehicle[];

      return { meta, data };
    }
  })();

  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = [
    {
      key: "timestamp.created",
      type: PageFilterTypes.DATE,
      displayName: "objects.timestamp.created"
    },
    {
      key: "state",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(VehicleStateEnum).map((e) => {
          return {
            text: `enums.VehicleStateEnum.${e}`,
            value: e
          };
        }),
        itemValue: "value"
      },
      displayName: "objects.vehicle.state"
    },
    { key: "numberplate", type: PageFilterTypes.STRING, displayName: "objects.vehicle.registration.numberplate" },
    {
      key: "identificationnumber",
      type: PageFilterTypes.STRING,
      displayName: "objects.vehicle.registration.identificationnumber"
    },
    {
      key: "registration.manufacturerDescription",
      type: PageFilterTypes.STRING,
      displayName: "objects.vehicle.registration.manufacturerDescription"
    },
    {
      key: "registration.manufacturerName",
      type: PageFilterTypes.STRING,
      displayName: "objects.vehicle.registration.manufacturerName"
    },
    {
      key: "ownership",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(VehicleOwnershipStatusEnum).map((e) => {
          return {
            text: `enums.VehicleOwnershipStatusEnum.${e}`,
            value: e
          };
        }),
        itemValue: "value"
      },
      displayName: "objects.vehicle.ownership"
    },
    {
      key: "financingType",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(VehicleFinancingTypeEnum).map((e) => {
          return {
            text: `enums.VehicleFinancingTypeEnum.${e}`,
            value: e
          };
        }),
        itemValue: "value"
      },
      displayName: "objects.vehicle.financingType"
    },
    {
      key: "note",
      type: PageFilterTypes.STRING,
      displayName: "objects.vehicle.note"
    },
    {
      key: "registration.fuelType",
      type: PageFilterTypes.STRING,
      displayName: "objects.vehicle.registration.fuelType"
    },
    { key: "costCenter", type: PageFilterTypes.STRING, displayName: "objects.vehicle.costCenter" }
  ].map((f) => new PaginationFilterListElement(f));

  _current: MrfiktivVehicleViewModelGen | null = null;

  get current() {
    return this._current;
  }

  @Mutation
  _mutateCurrent(data: MrfiktivVehicleViewModelGen) {
    this._current = data;
  }

  @Action
  setCurrent(data: MrfiktivVehicleViewModelGen) {
    this.context.commit("_mutateCurrent", data);
  }

  @Action
  async getOne(data: { partnerId: string; fleetId: string; id: string }): Promise<MrfiktivVehicleViewModelGen> {
    const response = await vehicleFleetService.findOne(data.partnerId, data.fleetId, data.id);

    this.context.commit("_mutateCurrent", response.data);

    return response.data;
  }

  @Action
  async getContractsByVehicle(data: {
    partnerId: string;
    fleetId: string;
    vehicleId: string;
  }): Promise<MrfiktivVehicleContractViewModelGen[]> {
    return fleetVehicleContractService.getByVehicle(data.partnerId, data.fleetId, data.vehicleId);
  }
}

export const VehicleFleetModule = getModule(VehicleFleetStore);
